import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { getActiveAccountSubdomain, setActiveAccountSubdomain } from '../services/storage';

interface AuthContextType {
  isLoggedIn: boolean;
  username: string | null;
  login: (subdomain: string) => void;
  logout: () => void;
  refresh: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [username, setUsername] = useState<string | null>(null);

  const refresh = useCallback(() => {
    const subdomain = getActiveAccountSubdomain();
    setUsername(subdomain);
  }, []);

  const login = useCallback((subdomain: string) => {
    setActiveAccountSubdomain(subdomain);
    setUsername(subdomain);
  }, []);

  const logout = useCallback(() => {
    setActiveAccountSubdomain(null);
    setUsername(null);

    // Tell service worker to clear caches and IndexedDB
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'LOGOUT' });
    }
  }, []);

  // Initial load
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Listen for storage changes (cross-tab sync)
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'fairdrop_active_account') {
        refresh();
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [refresh]);

  const value: AuthContextType = {
    isLoggedIn: !!username,
    username,
    login,
    logout,
    refresh,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
